import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Radar from 'radar-sdk-js'
import { Popup } from 'maplibre-gl'
import ReactDOMServer, { renderToStaticMarkup } from 'react-dom/server'
import useStoreLocatorContext from '@context/storeLocatorContext/useStoreLocatorContext'
import StoreInfoV1 from '../store-info'
import StoreInfoV2 from '../../store-locator-v2/store-info'

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'

const determineMarkerDataArray = stores => {
  const array = []

  array.push(
    ...stores.map((store, index) => ({
      id: store.StoreNumber,
      position: {
        lat: store.lat,
        lng: store.lng,
      },
      letter: LETTERS[index],
      ...store,
    })),
  )

  return array
}

const addStoreMarkerToMap = (map, markerData, selectStore, setMarkers, variant) => {
  if (!map) return

  const { position, id } = markerData
  const { lat, lng } = position

  // Creates a popup for the marker with the store information
  // and an on open listener that selects the store
  const popup = new Popup({ offset: 22 })
  popup.setHTML(storeMarkerPopupHtmlString(markerData, variant))
  popup.on('open', () => {
    selectStore(id)
  })

  // Creates a Radar marker element with custom pin element, position, and popup
  const mapMarker = Radar.ui
    .marker({ element: storeMarkerPin(markerData.letter) })
    .setLngLat([lng, lat])
    .setPopup(popup)

  // Adds the marker to the map and stores its instance in the markers context
  mapMarker.addTo(map)
  setMarkers(mapMarker, markerData.id, markerData.letter)
}

// Custom DOM svg element for the store marker
const storeMarkerPin = storeLetter => {
  const svgElement = document.createElement('div')
  const svgJsx = (
    <svg viewBox="0 0 24 24" style={{ color: '#0053a0', width: '48px', fontWeight: 500 }}>
      <path d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7z" fill="currentColor" />
      <text x={12} y={13.5} fill="#FFF" fontSize=".625em" textAnchor="middle">
        {storeLetter}
      </text>
    </svg>
  )
  svgElement.innerHTML = renderToStaticMarkup(svgJsx)
  return svgElement
}

// Custom HTML string for the store marker popup
const storeMarkerPopupHtmlString = (markerData, variant) => {
  const popupJsx =
    variant === 'store-locator-modal' ? (
      <StoreInfoV2 selectedMarker={markerData} />
    ) : (
      <StoreInfoV1 selectedMarker={markerData} />
    )

  return ReactDOMServer.renderToStaticMarkup(popupJsx)
}

const RadarMarkers = ({ variant }) => {
  const [loadMarkers, setLoadMarkers] = useState(true)
  const map = useStoreLocatorContext(state => state.mapInstance)
  // const { myStore } = useMyStore()
  const stores = useStoreLocatorContext(state => state.stores)
  const selectedStore = useStoreLocatorContext(state => state.selectedStore)
  const selectStore = useStoreLocatorContext(state => state.selectStore)
  const setMarkers = useStoreLocatorContext(state => state.setMarkers)
  const clearMarkers = useStoreLocatorContext(state => state.clearMarkers)
  const markers = useStoreLocatorContext(state => state.markers)

  // This effect adds the markers to the map when the map and stores are loaded
  useEffect(() => {
    if (map?.loaded && stores && loadMarkers) {
      const markersDataArray = determineMarkerDataArray(stores)

      markersDataArray?.forEach((marker, index) => {
        addStoreMarkerToMap(map, marker, selectStore, setMarkers, variant)
      })
      setLoadMarkers(false)
    }
  }, [map, stores, selectStore, setMarkers, loadMarkers, setLoadMarkers, variant])

  // This effect clears the markers when the stores change (e.g. when the user searches for a new location)
  useEffect(() => {
    if (!loadMarkers && !!markers) {
      // remove all markers from the map
      Object.keys(markers).forEach(key => {
        markers[key].instance.remove()
      })
      // remove marker instances from the context
      clearMarkers()
      // start process of loading the next batch of markers
      setLoadMarkers(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores])

  // This effect marks the selected store with a different color and opens the popup
  useEffect(() => {
    if (selectedStore && markers?.[selectedStore.storeNumber]) {
      Object.keys(markers).forEach(storeId => {
        if (parseInt(storeId) === selectedStore.storeNumber) {
          // We make sure the selected store pin is red and the popup is open
          markers[storeId].instance.addClassName('selected-marker')
          if (!markers[selectedStore.storeNumber]?.instance.getPopup()?.isOpen()) {
            markers[selectedStore.storeNumber].instance.togglePopup()
          }
        } else {
          // We make sure the other store pins are not red and the popup is closed
          markers[storeId].instance.removeClassName('selected-marker')
          if (markers[storeId]?.instance.getPopup()?.isOpen()) {
            markers[storeId].instance.togglePopup()
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStore])

  return <></>
}

RadarMarkers.propTypes = {
  variant: PropTypes.string,
}

export default RadarMarkers
